import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import type { Document } from '@gitbook/slate';
import {
    Popover,
    type PopoverProps,
    PopoverRoot,
    PopoverTrigger,
    Skeleton,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { useNavigationState } from '../../../navigation';
import { apiQuery } from '../../../state';
import { UserAvatarSkeleton } from '../../Users';
import { CommentThreadNav } from '../buttons';
import { CommentCard } from '../CommentCard';
import { CommentInput } from '../CommentInput';
import { useCommentActions } from '../useCommentActions';
import { type CommentNodeTarget, type UseCommentsProps, useComments } from '../useComments';
import { PopoverContainer } from './PopoverContainer';

export type NodeCommentsPopoverProps = UseCommentsProps & {
    /** Popover placement */
    placement?: PopoverProps['placement'];
    /** Trigger element */
    trigger: React.ReactNode;
};

/**
 * Popover that displays existing comments for a specific node in a scrollable list.
 */
export function NodeCommentsPopover(props: NodeCommentsPopoverProps) {
    const { nodeKey, tableCell, placement = 'right-start', trigger } = props;

    // Get the comment state and handlers
    const {
        nodeState,
        activeComment,
        submitComment,
        openCommentThread,
        nodeComments,
        closePopover,
    } = useComments({ nodeKey, tableCell });

    return (
        <PopoverRoot
            isOpen={nodeState.readPopoverIsOpen}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    closePopover();
                }
            }}
        >
            <PopoverTrigger>{trigger}</PopoverTrigger>
            <Popover placement={placement} sticky>
                <React.Suspense fallback={<NodeCommentsPopoverSkeleton />}>
                    {nodeState.readPopoverIsOpen ? (
                        <NodeCommentsPopoverContent
                            activeComment={activeComment}
                            openCommentThread={openCommentThread}
                            nodeComments={nodeComments}
                            submitComment={submitComment}
                            closePopover={closePopover}
                            nodeKey={nodeKey}
                        />
                    ) : null}
                </React.Suspense>
            </Popover>
        </PopoverRoot>
    );
}

function NodeCommentsPopoverContent(props: {
    activeComment: api.Comment | null;
    openCommentThread: (comment: api.Comment | null) => void;
    nodeComments: api.Comment[];
    submitComment: (body: Document) => Promise<void>;
    closePopover: () => void;
    nodeKey: CommentNodeTarget;
}) {
    const { activeComment, openCommentThread, nodeComments, submitComment, closePopover, nodeKey } =
        props;

    const { screen, overlay } = useNavigationState();
    const spaceScreen =
        overlay?.type === 'space' ? overlay : screen.type === 'space' ? screen : null;
    assertIsDefined(spaceScreen, 'Space should be defined in a `space` screen or overlay');

    const [{ data: space }, { data: changeRequest }] = useSuspenseQueries({
        queries: [
            apiQuery('getSpaceById', { spaceId: spaceScreen.space }),
            apiQuery(
                'getChangeRequestById',
                spaceScreen.changeRequest
                    ? {
                          spaceId: spaceScreen.space,
                          changeRequestId: String(spaceScreen.changeRequest),
                      }
                    : skipToken
            ),
        ],
    });

    return (
        <PopoverContainer>
            {activeComment ? (
                <SelectedNodeCommentView
                    space={space}
                    changeRequest={changeRequest}
                    activeComment={activeComment}
                    openCommentThread={openCommentThread}
                />
            ) : (
                <NodeCommentsList
                    comments={nodeComments}
                    activeComment={activeComment}
                    openCommentThread={openCommentThread}
                    nodeKey={nodeKey}
                    submitComment={submitComment}
                    closePopover={closePopover}
                    space={space}
                    changeRequest={changeRequest}
                />
            )}
        </PopoverContainer>
    );
}

/**
 * Displays a single comment in the popover.
 */
function SelectedNodeCommentView(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    activeComment: api.Comment;
    openCommentThread: (comment: api.Comment | null) => void;
}) {
    const { space, changeRequest, activeComment, openCommentThread } = props;
    const commentActions = useCommentActions({ comment: activeComment });

    return (
        <>
            <CommentThreadNav
                onPress={() => openCommentThread(null)}
                className="border-base border-b-0.5 p-2"
            />

            <div className="scrollbar scroll-gradient-mask-y relative flex max-h-128 flex-col overflow-y-auto">
                <CommentCard
                    key={activeComment.id}
                    space={space}
                    changeRequest={changeRequest}
                    comment={activeComment}
                    isSelected
                />
            </div>
            {commentActions.onReply ? (
                <CommentInput
                    testID="comment.replyInput"
                    placeholder="Reply in thread..."
                    targetNode={activeComment.id}
                    onSubmit={commentActions.onReply}
                    maxRows={20}
                    className="border-base border-t-0.5"
                />
            ) : null}
        </>
    );
}

/**
 * Default view showing a list of all the comments for the current node.
 */
function NodeCommentsList(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    comments: api.Comment[];
    activeComment: api.Comment | null;
    openCommentThread: (comment: api.Comment | null) => void;
    nodeKey: CommentNodeTarget;
    submitComment: (body: Document) => Promise<void>;
    closePopover: () => void;
}) {
    const {
        space,
        changeRequest,
        comments,
        openCommentThread,
        submitComment,
        nodeKey,
        closePopover,
    } = props;

    return (
        <>
            <div className="scroll-gradient-mask-y relative flex max-h-128 flex-col overflow-y-auto">
                {comments.map((comment: api.Comment) => (
                    <CommentCard
                        key={comment.id}
                        space={space}
                        changeRequest={changeRequest}
                        comment={comment}
                        isSelected={false}
                        onPress={openCommentThread}
                        className="border-base border-b last:border-b-0 hover:bg-side"
                    />
                ))}
            </div>
            <CommentInput
                testID="comment.postNodeComment"
                placeholder="Leave a comment..."
                targetNode={nodeKey}
                onSubmit={submitComment}
                onCancel={closePopover}
                className="border-base border-t-0.5"
            />
        </>
    );
}

function NodeCommentsPopoverSkeleton() {
    return (
        <div className="flex h-fit min-w-76 max-w-76 flex-col gap-3 overflow-hidden rounded-lg border border-base bg-base shadow-subtle">
            {/* Comments area */}
            <div className="flex max-h-80 flex-col gap-3 overflow-y-auto p-3">
                <div className="flex gap-2">
                    <UserAvatarSkeleton size="sm" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-1/3" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                        <div className="flex items-center gap-2">
                            <UserAvatarSkeleton size="sm" /> <Skeleton className="h-4 w-16" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Comment input area */}
            <div className="flex flex-col gap-2 p-2">
                <div className="flex items-center gap-2">
                    <UserAvatarSkeleton size="sm" />
                    <Skeleton className="h-9 flex-1 rounded-lg" />
                </div>
                {/* Comment actions */}
                <div className="ml-8 flex flex-1 items-center justify-between gap-2">
                    <div className="flex gap-1">
                        <Skeleton className="h-6 w-6" />
                        <Skeleton className="h-6 w-6" />
                        <Skeleton className="h-6 w-6" />
                        <Skeleton className="h-6 w-6" />
                    </div>
                    <Skeleton className="h-6 w-16" />
                </div>
            </div>
        </div>
    );
}
